import React, { FunctionComponent, useEffect, useMemo, useState } from 'react'
import { useTranslation } from '../../utils/convergence'
import {
  authLogout,
  clearPaymentOptionsError,
  getPaymentOptions,
  selectPurchaseMethod,
  setCheckoutStatus,
  setShowEfaModal,
  useAppDispatch,
  useAppropriatePaymentMethod,
  useAppRouter,
  useConsumerLendingEligible,
  useConsumerLendingState,
  useCountry,
  useIsCreditFreezeDeclineFlow,
  useIsExtendedFraudAlert,
  usePaymentOptionsError,
  usePreselectedPaymentOption,
  useShowEfaModal,
  verifyEFARobocallEvent,
} from '../../state'
import { CheckoutStatus, PaymentOptionsStatus, VerifyEFARobocallEventRequest } from '../../model'
import { Heading, Loader, Text, useIsBreakpointLargeAndUp } from '@afterpaytouch/core'
import { ConsumerWelcome } from '../PaymentTypes/ConsumerWelcome'
import { Route, useSummaryRoute } from '../../config/router'
import styles from './style.module.css'
import { TrackingEvent } from '../../model/monitoring'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { PaymentOptionsRadioGroup } from './PaymentOptionsRadioGroup'
import { PaymentOptionsContinueButton } from './PaymentOptionsContinueButton'
import { useHandlePaymentOptionsError, useSelectedPaymentOptionState, useSendEFARobocallEvent } from './hooks'
import { EfaRobocallModal } from '../EfaRobocallModal'
import { handlePaymentOptionData } from './utils'
import { PaymentType } from '@afterpay/types'

export const PaymentOptions: FunctionComponent = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const router = useAppRouter()
  const country = useCountry()
  const preselectedPaymentOption = usePreselectedPaymentOption()
  const [lock, setLock] = useState<boolean>(false)
  const { paymentOptionsStatus, paymentOptions } = useConsumerLendingState()
  const isConsumerLendingEligible = useConsumerLendingEligible()
  const [selectedPaymentOption, setSelectedPaymentOption] = useSelectedPaymentOptionState()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const isDesktop = useIsBreakpointLargeAndUp()
  const summaryRoute = useSummaryRoute()
  const [paymentOptionsUpdated, setPaymentOptionsUpdated] = useState(false)
  const paymentMethod = useAppropriatePaymentMethod()
  const paymentOptionsError = usePaymentOptionsError()
  const { handlePaymentOptionsError } = useHandlePaymentOptionsError()
  const isExtendedFraudAlert = useIsExtendedFraudAlert()
  const isCreditFreezeDeclineFlow = useIsCreditFreezeDeclineFlow()
  const {
    onSuccess: sendEFARobocallEventSuccess,
    onError: sendEFARobocallEventError,
    isSuccess: isEFARobocallEventSuccess,
    isDecline: isEFARobocallEventDecline,
  } = useSendEFARobocallEvent()
  const showEfaModal = useShowEfaModal()
  const shouldShowEfaModal = isExtendedFraudAlert && showEfaModal

  const shouldShowLoader = paymentOptionsStatus < PaymentOptionsStatus.Fetched

  const sendEFARobocallEvent = async (request: VerifyEFARobocallEventRequest): Promise<void> => {
    await dispatch(verifyEFARobocallEvent(request))
  }

  // PBI, PCL-3M, PCL-6M, PCL-12M, PCL-24M, or None
  const preSelectedEventValue = useMemo<string>(() => {
    if (preselectedPaymentOption?.paymentType === PaymentType.PBI) {
      return 'PBI'
    } else if (preselectedPaymentOption?.paymentType === PaymentType.PCL) {
      return `PCL-${preselectedPaymentOption?.installmentsCount}M`
    }

    return 'None'
  }, [preselectedPaymentOption])

  useEffect(() => {
    router.prefetch(Route.SUMMARY)
    router.prefetch(Route.ADD_PAYMENT_METHOD)
    router.prefetch(Route.ENTER_EMAIL)
  }, [])

  useEffect(() => {
    if (isExtendedFraudAlert && !isEFARobocallEventSuccess) {
      sendEFARobocallEvent({
        onSuccess: sendEFARobocallEventSuccess,
        onError: sendEFARobocallEventError,
      })
    }
  }, [isExtendedFraudAlert, isEFARobocallEventSuccess])

  useEffect(() => {
    const doGetPaymentOptions = async (): Promise<void> => {
      try {
        await dispatch(getPaymentOptions()).unwrap()
      } catch (e) {
        handlePaymentOptionsError(e)
      }
    }

    const shouldLoadPaymentOptionsAU = isConsumerLendingEligible && country === 'AU'
    const shouldLoadPaymentOptionsUS = isConsumerLendingEligible && country === 'US'

    if (shouldLoadPaymentOptionsAU || shouldLoadPaymentOptionsUS) {
      doGetPaymentOptions()
    }
  }, [isEFARobocallEventSuccess])

  useEffect(() => {
    if (paymentOptionsStatus === PaymentOptionsStatus.Fetched) {
      logEvent(TrackingEvent.VIEWED_CL_PAYMENT_OPTIONS_PAGE, {
        paymentOption: handlePaymentOptionData(paymentOptions),
        preSelected: preSelectedEventValue,
      })
    }
  }, [preSelectedEventValue, paymentOptionsStatus, paymentOptions])

  useEffect(() => {
    // during extended fraud alert flow, should clear consumerLending.error state
    // after fetching full payment option list from get-payment-option endpoint
    if (paymentOptionsStatus === PaymentOptionsStatus.Fetched) {
      dispatch(clearPaymentOptionsError())
    }
  }, [paymentOptionsStatus])

  useEffect(() => {
    const logoutUser = async (): Promise<void> => {
      await dispatch(authLogout())
      router.push(Route.ENTER_EMAIL)
    }

    // if it is fraud in efa flow, redirect to login page
    if (isEFARobocallEventDecline) {
      logoutUser()
    }

    if (isEFARobocallEventSuccess) {
      dispatch(setShowEfaModal(false))
    }
  }, [isEFARobocallEventSuccess, isEFARobocallEventDecline])

  useEffect(() => {
    if (shouldShowEfaModal || isCreditFreezeDeclineFlow) {
      return
    }
    if (paymentOptionsUpdated || paymentOptionsError !== undefined) {
      if (paymentMethod !== null) {
        dispatch(selectPurchaseMethod(paymentMethod, false))
      } else {
        dispatch(setCheckoutStatus(CheckoutStatus.LackOfEligiblePaymentMethod))
        router.push(Route.ADD_PAYMENT_METHOD)
        return
      }

      router.push(summaryRoute)
    }
  }, [paymentOptionsUpdated, paymentOptionsError])

  useEffect(() => {
    if (preselectedPaymentOption != null) {
      setSelectedPaymentOption(preselectedPaymentOption)
    }
  }, [preselectedPaymentOption])

  if (shouldShowLoader) {
    return (
      <div {...((isDesktop ?? false) && { className: styles.paymentOptionsLoadingWrapperDesktop })}>
        {shouldShowEfaModal && <EfaRobocallModal isError={shouldShowEfaModal} />}
        <Heading size='XL'>{t(`consumerLending:loadPaymentOptions:title`)}</Heading>
        <div className={styles.loadPaymentOptionsText}>
          <Text>{t('consumerLending:loadPaymentOptions:message')}</Text>
        </div>
        <Loader />
      </div>
    )
  }

  return (
    <>
      <ConsumerWelcome />
      <div className={isDesktop ?? false ? styles.paymentOptionsWrapperDesktop : styles.paymentOptionsWrapperMobile}>
        <PaymentOptionsRadioGroup lock={lock} selectedPaymentOptionLocal={selectedPaymentOption} setSelectedPaymentOptionLocal={setSelectedPaymentOption} />
        <PaymentOptionsContinueButton
          lock={lock}
          setLock={setLock}
          selectedPaymentOptionLocal={selectedPaymentOption}
          setPaymentOptionsUpdated={setPaymentOptionsUpdated}
        />
      </div>
    </>
  )
}
